import React from 'react'
import propTypes from 'prop-types'

import { Card, CardHeading, CardContent, CardFooter } from '@components/layout/Card'
import { RadialBarChart } from '@components/charts/RadialBarChart'
import { Grid } from '@UILibrary/Grid'
import { Text } from '@UILibrary/Text'
import { Icon } from '@UILibrary/Icon'

import { formatPercentageChange } from '@utils/stringMinipulation'

import { StarRating } from '../Shared/StarRating'
import { LearnMore } from '../Shared/LearnMore'
import { DataPlaceholder } from '../Shared/DataPlaceholder'

import {
  buildRadialBarChartDataProp,
} from './charts/data'

import styles from './overview.module.sass'
import { ProgressComparison } from './components'

export const Overview = (props) => {
  const { average_start_score, average_current_score, score_improvement_percentage } = props.financialHealthData
  const { received_coaching, workshops_attended, app_logins } = props.programEngagementBreakdown

  const { text: currentHealthText, color: currentHealthColor } = formatPercentageChange(score_improvement_percentage)

  const hasProgramEngagementData = props.programEngagementBreakdown

  return (
    <div>
      <Grid cols="3" gap="m">
        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Average financial health score</Text>
            <Text body as="p">
              <Text body as="strong" colour={currentHealthColor}>{currentHealthText}</Text> improvement since each participant's start
            </Text>
          </CardHeading>
          <CardContent>
            <ProgressComparison
              startValue={average_start_score}
              currentValue={average_current_score}
            />
          </CardContent>
          <CardFooter>
            <LearnMore
              buttonText="How is the score calculated?"
              heading="Financial Health Per Cohort"
              subHeading="Understanding the financial health of your organisation is critical."
            >
              <Text body as="p">
                In simplest terms, the financial health score looks at how much of a person's expenses they would be able to cover from investments and other sources should they one day stop working. This is typically referred to as their "replacement ratio".
              </Text>
              <Text body as="p">
                This number is a result of every decision that they make on their financial journey.
              </Text>
              <Text body as="p">
                We calculate this score for each individual, then we calculate the average for the cohort, then the average for all cohorts together.
              </Text>
              <Text body as="p">
                This metric will expand in the future, taking into consideration more aspects of each person's personal financial journey.
              </Text>
              <Text body as="p">
                The financial health score is dependent on the accuracy of the data entered into the system.
              </Text>
            </LearnMore>
          </CardFooter>
        </Card>

        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3">Engagement with the programme</Text>
          </CardHeading>
          <CardContent>
            <Grid cols="2-fixed-200" gap="m">
              {hasProgramEngagementData ? (
                <>
                  <RadialBarChart
                    data={buildRadialBarChartDataProp(props.programEngagementBreakdown)}
                    options={{
                      plugins: {
                        tooltip: {
                          enabled: false
                        }
                      }
                    }}
                  />
                  <div style={{ paddingTop: '2rem' }}>
                    <Text as='p' margin={{ bottom: 'm' }}><Icon name="block" size="small" color="secondary-main" /> <Text bodyLarge as='strong'>{received_coaching}%</Text> received coaching</Text>
                    <Text as='p' margin={{ bottom: 'm' }}><Icon name="block" size="small" color="primary-main" /> <Text bodyLarge as='strong'>{workshops_attended}%</Text> workshops</Text>
                    <Text as='p' margin={{ bottom: 'm' }}><Icon name="block" size="small" color="tertiary-main" /> <Text bodyLarge as='strong'>{app_logins}%</Text> claimed the app</Text>
                  </div>
                </>
              ) : (
                <DataPlaceholder />
              )}
            </Grid>
          </CardContent>
        </Card>

        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin='none'>Average program rating</Text>
          </CardHeading>
          <CardContent>
            {props.programFeedback.average_rating > 0 ? (
              <div className='mauto'>
                <Text title1b as="h1" className={styles.ratingText} colour="secondary-main" textAlign="center">{props.programFeedback.average_rating}</Text>
                <StarRating rating={props.programFeedback.average_rating} />
                <Text body as="p" textAlign="center">{props.programFeedback.feedback_count} ratings</Text>
              </div>
            ) : (
              <DataPlaceholder />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid cols="3-2" gap="m">
      </Grid>
    </div>
  )
}

Overview.propTypes = {
  containerStyle: propTypes.string,
  chartStyle: propTypes.string,
  financialHealthData: propTypes.object,
  improvementSinceStartData: propTypes.object,
  incomeReplacementRatioData: propTypes.object,
}
